import { LoadingOutlined } from "@ant-design/icons";
import { Button, DatePicker, Form, Input, Modal, Select, Spin, TreeSelect, Divider, Tag } from "antd";
import React, { useEffect, useState } from "react";
import CreateTaskApi from "../../../api/tasks/CreateTaskApi";
import { GetUnitPositionsApi2 } from "../../../api/units/GetUnitPositionsApi2";
import { GetTreeSelectUnitsApi } from "../../../api/GetTreeSelectUnitsApi";
import { 
  getClickUpStatus, 
  getClickUpTeams, 
  getClickUpSpaces, 
  getClickUpLists, 
  getClickUpTeamMembers 
} from "../../../api/clickup/ClickUpApi";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../../helpers/NotificationHelpers";

const { Option } = Select;

function AddTaskModal(props) {
  const [form] = Form.useForm();
  const [submit, setSubmit] = useState(false);
  const [unitPositions, setUnitPositions] = useState([]);
  const [unitsTree, setUnitsTree] = useState([]);
  
  // ClickUp Integration State
  const [clickUpEnabled, setClickUpEnabled] = useState(false);
  const [clickUpTeams, setClickUpTeams] = useState([]);
  const [clickUpSpaces, setClickUpSpaces] = useState([]);
  const [clickUpLists, setClickUpLists] = useState([]);
  const [clickUpMembers, setClickUpMembers] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [loadingClickUp, setLoadingClickUp] = useState(false);

  useEffect(() => {
    if (props.visible) {
      getUnitsTree();
      checkClickUpStatus();
      // If unitId is provided as prop, pre-select it and load positions
      if (props.unitId) {
        form.setFields([{ name: "unit", value: props.unitId }]);
        loadUnitPositions(props.unitId);
      }
    }
  }, [props.visible, props.unitId]);

  async function checkClickUpStatus() {
    try {
      const statusResponse = await getClickUpStatus();
      
      if (statusResponse.success && statusResponse.data.enabled) {
        setClickUpEnabled(true);
        await loadClickUpTeams();
      } else {
        setClickUpEnabled(false);
      }
    } catch (error) {
      console.error("Failed to check ClickUp status:", error);
      setClickUpEnabled(false);
    }
  }

  async function loadClickUpTeams() {
    try {
      setLoadingClickUp(true);
      const teamsResponse = await getClickUpTeams();
      if (teamsResponse.success) {
        setClickUpTeams(teamsResponse.data);
        // Auto-select first team if only one exists
        if (teamsResponse.data.length === 1) {
          const teamId = teamsResponse.data[0].id;
          setSelectedTeam(teamId);
          form.setFields([{ name: "clickupTeam", value: teamId }]);
          await loadClickUpSpaces(teamId);
          await loadClickUpMembers(teamId);
        }
      }
    } catch (error) {
      console.error("Failed to load ClickUp teams:", error);
    } finally {
      setLoadingClickUp(false);
    }
  }

  async function loadClickUpSpaces(teamId) {
    try {
      const spacesResponse = await getClickUpSpaces(teamId);
      if (spacesResponse.success) {
        setClickUpSpaces(spacesResponse.data);
      }
    } catch (error) {
      console.error("Failed to load ClickUp spaces:", error);
    }
  }

  async function loadClickUpLists(spaceId) {
    try {
      const listsResponse = await getClickUpLists(spaceId);
      if (listsResponse.success) {
        setClickUpLists(listsResponse.data);
      }
    } catch (error) {
      console.error("Failed to load ClickUp lists:", error);
    }
  }

  async function loadClickUpMembers(teamId) {
    try {
      const membersResponse = await getClickUpTeamMembers(teamId);
      if (membersResponse.success) {
        setClickUpMembers(membersResponse.data);
      }
    } catch (error) {
      console.error("Failed to load ClickUp members:", error);
    }
  }

  async function handleClickUpTeamChange(teamId) {
    setSelectedTeam(teamId);
    setClickUpSpaces([]);
    setClickUpLists([]);
    setClickUpMembers([]);
    form.setFields([
      { name: "clickupSpace", value: null },
      { name: "clickupList", value: null },
      { name: "clickupAssignee", value: null },
    ]);
    
    if (teamId) {
      await loadClickUpSpaces(teamId);
      await loadClickUpMembers(teamId);
    }
  }

  async function handleClickUpSpaceChange(spaceId) {
    setSelectedSpace(spaceId);
    setClickUpLists([]);
    form.setFields([
      { name: "clickupList", value: null },
    ]);
    
    if (spaceId) {
      await loadClickUpLists(spaceId);
    }
  }

  async function getUnitsTree() {
    const apiResponse = await GetTreeSelectUnitsApi();
    if (apiResponse.message === "success") {
      setUnitsTree(apiResponse.units);
    }
  }

  async function loadUnitPositions(unitId) {
    const apiResponse = await GetUnitPositionsApi2(unitId);
    if (apiResponse.success) {
      setUnitPositions(apiResponse.data || []);
    }
  }

  async function selectUnit(value) {
    const apiResponse = await GetUnitPositionsApi2(value);
    if (apiResponse.success) {
      setUnitPositions(apiResponse.data);
      form.setFields([
        {
          name: "assignTo",
          value: "",
        },
      ]);
    }
  }

  function handleCancel() {
    form.resetFields();
    setUnitPositions([]);
    setUnitsTree([]);
    // Reset ClickUp state
    setClickUpSpaces([]);
    setClickUpLists([]);
    setClickUpMembers([]);
    setSelectedTeam(null);
    setSelectedSpace(null);
    props.handleCancel();
  }
  async function onFinish(values) {
    setSubmit(true);
    
    // Prepare ClickUp data if fields are filled
    let clickUpData = null;
    if (clickUpEnabled && values.clickupList && values.clickupSpace) {
      clickUpData = {
        spaceId: values.clickupSpace,
        listId: values.clickupList,
        assigneeId: values.clickupAssignee || null,
        priority: values.clickupPriority || 3,
        tags: values.clickupTags || [],
      };
    }

    const apiResponse = await CreateTaskApi(
      props.planId,
      values.name,
      values.targetDate,
      values.startDate,
      values.assignTo,
      clickUpData
    );

    if (apiResponse.statusCode) {
      openNotificationError(
        "bottomRight",
        "Couldn't add task. Try again later."
      );
    } else if (apiResponse.success) {
      const message = clickUpData 
        ? "Task added successfully and synced to ClickUp" 
        : "Task added successfully";
      openNotificationSuccess("bottomRight", message);
      props.handleOk();
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't add task. Try again later."
      );
    }
    handleCancel();
    setSubmit(false);
  }

  return (
    <Modal
      title="Add Task"
      visible={props.visible}
      onCancel={() => handleCancel()}
      footer={null}
      destroyOnClose={true}
    >
      <Form
        name="addTask"
        onFinish={onFinish}
        autoComplete="off"
        layout="vertical"
        requiredMark={false}
        form={form}
      >
        <Form.Item
          label="Task*"
          name="name"
          rules={[
            {
              required: true,
              message: "Please enter a Task!",
            },
          ]}
        >
          <Input placeholder="Task name" />
        </Form.Item>
        <Form.Item label="Start date" name="startDate">
          <DatePicker className="w-full" />
        </Form.Item>
        <Form.Item
          label="Target date*"
          name="targetDate"
          rules={[
            {
              required: true,
              message: "Please enter target date!",
            },
          ]}
        >
          <DatePicker className="w-full" />
        </Form.Item>
        <Form.Item
          label="Unit*"
          name="unit"
          rules={[
            {
              required: true,
              message: "Please select a unit!",
            },
          ]}
        >
          <TreeSelect
            showSearch
            treeLine={true}
            dropdownStyle={{
              maxHeight: 400,
              overflow: "auto",
            }}
            treeData={unitsTree}
            placeholder="Select unit"
            treeDefaultExpandAll
            treeNodeFilterProp={"title"}
            showIcon={false}
            filterTreeNode
            onChange={(value) => selectUnit(value)}
          />
        </Form.Item>
        <Form.Item label="Assign to" name="assignTo">
          <Select
            showSearch
            placeholder="Select position"
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            filterSort={(optionA, optionB) =>
              optionA.children
                .toLowerCase()
                .localeCompare(optionB.children.toLowerCase())
            }
          >
            {unitPositions.map((position) => {
              return (
                <Option key={position.id} value={position.id}>
                  {(position.user?.firstName
                    ? position.user?.firstName + ", "
                    : "") + position.designation?.name}
                </Option>
              );
            })}
          </Select>
        </Form.Item>

        {/* ClickUp Integration Fields */}
        {clickUpEnabled && (
          <>
            <Divider>
              <span style={{ color: '#1890ff', fontWeight: 'bold' }}>
                ClickUp Integration <Tag color="blue">Optional</Tag>
              </span>
            </Divider>
            
            <Form.Item label="ClickUp Team" name="clickupTeam">
              <Select
                placeholder="Select ClickUp team"
                loading={loadingClickUp}
                onChange={handleClickUpTeamChange}
                allowClear
              >
                {clickUpTeams.map((team) => (
                  <Option key={team.id} value={team.id}>
                    {team.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="ClickUp Space" name="clickupSpace">
              <Select
                placeholder="Select ClickUp space"
                onChange={handleClickUpSpaceChange}
                disabled={!selectedTeam}
                allowClear
              >
                {clickUpSpaces.map((space) => (
                  <Option key={space.id} value={space.id}>
                    {space.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="ClickUp List" name="clickupList">
              <Select
                placeholder="Select ClickUp list"
                disabled={!selectedSpace}
                allowClear
              >
                {clickUpLists.map((list) => (
                  <Option key={list.id} value={list.id}>
                    {list.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="ClickUp Assignee" name="clickupAssignee">
              <Select
                placeholder="Select ClickUp assignee"
                disabled={!selectedTeam}
                allowClear
              >
                {clickUpMembers.map((member) => (
                  <Option key={member.id} value={member.id}>
                    {member.username} ({member.email})
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="ClickUp Priority" name="clickupPriority">
              <Select placeholder="Select priority" allowClear>
                <Option value={1}>🔴 Urgent</Option>
                <Option value={2}>🟡 High</Option>
                <Option value={3}>🟢 Normal</Option>
                <Option value={4}>⚪ Low</Option>
              </Select>
            </Form.Item>

            <Form.Item label="ClickUp Tags" name="clickupTags">
              <Select
                mode="tags"
                placeholder="Add tags (press Enter to add)"
                allowClear
              />
            </Form.Item>
          </>
        )}
        <Form.Item style={{ marginBottom: 0 }}>
          <div className=" flex justify-end">
            <Button className="mr-3 w-28" onClick={() => handleCancel()}>
              Cancel
            </Button>
            <Button
              className="btn w-28"
              type="primary"
              htmlType={submit && "submit"}
              disabled={submit}
            >
              {submit ? (
                <Spin
                  size="small"
                  indicator={<LoadingOutlined style={{ color: "#fff" }} spin />}
                />
              ) : (
                `Add`
              )}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default AddTaskModal;
